import React from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import configData from "../../config/config.json";
import usStates from "../../config/us_states_no_blank_entry.json";
import { formatPhone, formatInternationalPhone, formatZip } from "../common/function/formattingUtils.js";
import { validateUser, trimUserFields } from "../common/function/userValidation.js";
import BackButton from "../common/BackButton";
import Alert from "../common/modal/Alert";
import Loading from "../common/modal/Loading";
import { LOADING_STATUES, ALERT_TYPES, PASSWORD_REQUIREMENTS_HINT, USERNAME_REQUIREMENTS_HINT, PASSWORD_RULES } from "../constants/constants";
import { warningSmallIcon } from "../../img/font_awesome/font_awesome";

const initialFormState = (countyStateCode) => ({
  username: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  city: "",
  state: countyStateCode,
  zip: "",
  email: "",
  phoneFormatType: "STANDARD",
  phoneInternational: "",
  phoneStandard: "",
  password: "",
  passwordConfirm: "",
});

export default function RegisterSmall() {
  const history = useHistory();
  const countyStateCode = useSelector((state) => state.productPropertiesReducer.countyStateCode);
  const isNarrowScreen = window.innerWidth <= 360;
  const countyName = useSelector((state) => state.countyConfigCommonReducer.countyName);
  const appTitle = useSelector((state) => state.countyConfigCommonReducer.appTitle);

  const [form, setForm] = React.useState(() => initialFormState(countyStateCode));
  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState("");
  const [showAlert, setShowAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertType, setAlertType] = React.useState(ALERT_TYPES.OK);
  const [redirectToLogin, setRedirectToLogin] = React.useState(false);
  const fieldRefs = React.useRef({});

  const scrollToFirstError = (validationErrors) => {
    const firstKey = Object.keys(validationErrors)[0];
    const el = fieldRefs.current[firstKey];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    let newValue = value;

    if (id === "phoneStandard") {
      newValue = formatPhone(value, form.phoneStandard);
    } else if (id === "phoneInternational") {
      newValue = formatInternationalPhone(value);
    } else if (id === "zip") {
      newValue = formatZip(value);
    }

    setForm({ ...form, [id]: newValue });
    setErrors({});
  };

  const hideAlert = () => {
    setShowAlert(false);
    setAlertMessage("");
    setAlertType(ALERT_TYPES.OK);
    if (redirectToLogin) {
      history.push("/loginMobile");
    }
  };

  const validateAndSubmit = (trimmedForm) => {
    const validationErrors = validateUser(trimmedForm);
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToFirstError(validationErrors);
      return;
    }

    setStatus(LOADING_STATUES.LOADING);

    const { passwordConfirm, phoneInternational, phoneStandard, ...request } = trimmedForm;
    request.phone = trimmedForm.phoneFormatType === "INTERNATIONAL" ? phoneInternational : phoneStandard;

    axios.post(configData.LANDNOTIFICATION_SERVER_URL + "auth/signUp", request, { withCredentials: true })
      .then(() => {
        setStatus("");
        setRedirectToLogin(true);
        setAlertType(ALERT_TYPES.OK);
        setShowAlert(true);
        setAlertMessage("We've emailed you a link.  Please follow the link to complete your registration.");
      }, (error) => {
        setStatus("");
        setRedirectToLogin(false);
        setAlertType(ALERT_TYPES.WARNING);
        setShowAlert(true);
        setAlertMessage(error.response?.data?.message || "Something went wrong. Please try again.");
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const trimmedFields = trimUserFields(form);
    const trimmedForm = { ...form, ...trimmedFields };
    setForm(trimmedForm);
    validateAndSubmit(trimmedForm);
  };

  return (
    <div>
      {/* Banner */}
      <div style={styles.banner}>
        <div style={styles.logoContainer}>
          <BackButton onClick={() => history.goBack()} ariaLabel="Back" />
        </div>

        <div style={styles.titleContainer}>
          <div
            style={{
              ...styles.title,
              fontSize: isNarrowScreen ? "16px" : "20px",
            }}
          >
            {countyName}
          </div>
          <div
            style={{
              ...styles.subtitle,
              fontSize: isNarrowScreen ? "13px" : "16px",
            }}
          >
            {appTitle}
          </div>
        </div>

        <div style={styles.right} />
      </div>

      {/* Content */}
      <div style={styles.content}>
        <h2
          style={{
            ...styles.pageTitle,
            fontSize: isNarrowScreen ? "18px" : "20px",
          }}
        >
          New User Registration
        </h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          <Field id="username" label="Username" value={form.username} onChange={handleChange} error={errors.username} fieldRef={(el) => fieldRefs.current.username = el} hint={USERNAME_REQUIREMENTS_HINT} />
          <Field id="email" label="Email" type="email" value={form.email} onChange={handleChange} error={errors.email} fieldRef={(el) => fieldRefs.current.email = el} />

          <Field id="password" label="Password" type="password" autoComplete="new-password" passwordRules={PASSWORD_RULES} value={form.password} onChange={handleChange} error={errors.password} fieldRef={(el) => fieldRefs.current.password = el} hint={PASSWORD_REQUIREMENTS_HINT} />
          <Field id="passwordConfirm" label="Confirm Password" type="password" autoComplete="new-password" value={form.passwordConfirm} onChange={handleChange} error={errors.passwordConfirm} fieldRef={(el) => fieldRefs.current.passwordConfirm = el} />

          <Field id="firstName" label="First Name" value={form.firstName} onChange={handleChange} error={errors.firstName} fieldRef={(el) => fieldRefs.current.firstName = el} />
          <Field id="lastName" label="Last Name" value={form.lastName} onChange={handleChange} error={errors.lastName} fieldRef={(el) => fieldRefs.current.lastName = el} />

          <Field id="company" label="Company" value={form.company} onChange={handleChange} error={errors.company} fieldRef={(el) => fieldRefs.current.company = el} />
          <Field id="address" label="Address" value={form.address} onChange={handleChange} error={errors.address} fieldRef={(el) => fieldRefs.current.address = el} />
          <Field id="city" label="City" value={form.city} onChange={handleChange} error={errors.city} fieldRef={(el) => fieldRefs.current.city = el} />

          <div ref={(el) => fieldRefs.current.state = el}>
            <label style={styles.label} htmlFor="state">State</label>
            <select id="state" value={form.state} onChange={handleChange} className={errors.state ? "field-error" : undefined} style={styles.input}>
              {usStates.map((usState) => (
                <option key={usState.code} value={usState.code}>{usState.description}</option>
              ))}
            </select>
            {errors.state && <div style={styles.error}>{warningSmallIcon} {errors.state}</div>}
          </div>

          <Field id="zip" label="Zip" value={form.zip} onChange={handleChange} error={errors.zip} fieldRef={(el) => fieldRefs.current.zip = el} />

          <div>
            <label style={styles.label} htmlFor="phoneFormatType">Phone Format</label>
            <select id="phoneFormatType" value={form.phoneFormatType} onChange={handleChange} style={styles.input}>
              <option value="STANDARD">(XXX) XXX-XXXX</option>
              <option value="INTERNATIONAL">International</option>
            </select>
          </div>

          {form.phoneFormatType === "STANDARD" && (
            <Field id="phoneStandard" label="Phone" type="tel" value={form.phoneStandard} onChange={handleChange} error={errors.phoneStandard} fieldRef={(el) => fieldRefs.current.phoneStandard = el} />
          )}

          {form.phoneFormatType === "INTERNATIONAL" && (
            <Field id="phoneInternational" label="Phone (with country code)" type="tel" value={form.phoneInternational} onChange={handleChange} error={errors.phoneInternational} fieldRef={(el) => fieldRefs.current.phoneInternational = el} />
          )}

          <button type="submit" disabled={status === LOADING_STATUES.LOADING} style={styles.submitButton}>
            {status === LOADING_STATUES.LOADING ? "Submitting..." : "Register"}
          </button>
        </form>
      </div>

      <Loading loadingIndicator={status === LOADING_STATUES.LOADING} />
      <Alert show={showAlert} hideAlert={hideAlert} message={alertMessage} type={alertType} />
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, error, autoComplete, fieldRef, hint, passwordRules }) {
  const [showPassword, setShowPassword] = React.useState(false);
  const isPassword = type === "password";
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;

  return (
    <div ref={fieldRef}>
      <label style={styles.label} htmlFor={id}>{label}</label>
      <div style={isPassword ? styles.passwordRow : undefined}>
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          passwordrules={passwordRules}
          className={error ? "field-error" : undefined}
          style={isPassword ? styles.passwordInput : styles.input}
        />
        {isPassword && (
          <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.showHideButton}>
            {showPassword ? "Hide" : "Show"}
          </button>
        )}
      </div>
      {hint && <div style={styles.hint}>{hint}</div>}
      {error && <div style={styles.error}>{warningSmallIcon} {error}</div>}
    </div>
  );
}

const styles = {
  banner: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    backgroundColor: "#114E6B",
    color: "white",
    padding: "14px 16px",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    boxSizing: "border-box",
    overflowX: "hidden",
    zIndex: 1000,
  },

  logoContainer: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  titleContainer: {
    marginLeft: "8px",
    textAlign: "left",
  },

  title: {
    fontWeight: "700",
    fontSize: "20px",
    lineHeight: "1.2",
    color: "white",
  },

  subtitle: {
    fontSize: "16px",
    letterSpacing: "0.5px",
    opacity: 0.9,
    color: "white",
  },

  right: {
    justifySelf: "end",
    width: "40px",
  },

  content: {
    marginTop: "70px",
    padding: "16px",
  },

  pageTitle: {
    marginBottom: "20px",
    color: "#114E6B",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "bold",
    color: "#114E6B",
    marginBottom: "4px",
  },

  input: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  passwordRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  passwordInput: {
    flex: 1,
    padding: "10px",
    fontSize: "16px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },

  showHideButton: {
    backgroundColor: "white",
    color: "#114E6B",
    padding: "10px 12px",
    borderRadius: "6px",
    border: "1px solid #114E6B",
    fontWeight: "bold",
    fontSize: "13px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  error: {
    color: "red",
    fontWeight: "bold",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "2px",
  },

  hint: {
    color: "#6c757d",
    fontSize: "12px",
    marginTop: "4px",
  },

  submitButton: {
    width: "100%",
    marginTop: "8px",
    backgroundColor: "#114E6B",
    color: "white",
    padding: "10px",
    borderRadius: "6px",
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
  },
};
